import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CircleHelp,
  Monitor,
  Smartphone,
  Tablet,
  TrendingUp,
} from "lucide-react";
import { getAnalyticsBundleForPlanId } from "../../../data/dashboard-projects-dummy";
import {
  buildTopCountryRows,
  countryNameFromAlpha3,
} from "../../../utils/country-display";
import styles from "./DashboardAnalytics.module.css";

const GSC_BLUE = "#1a73e8";
const GSC_PURPLE = "#9333ea";

function deviceIconForName(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("mobile")) return Smartphone;
  if (n.includes("tablet")) return Tablet;
  if (n.includes("desktop")) return Monitor;
  return Monitor;
}

function DeviceYAxisTick({ x, y, payload }) {
  const label = payload?.value ?? "";
  const Icon = deviceIconForName(label);
  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject
        x={-92}
        y={-11}
        width={90}
        height={22}
        style={{ overflow: "visible" }}
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          className={styles.deviceTickInner}
        >
          <Icon size={16} strokeWidth={2} aria-hidden className={styles.deviceTickIcon} />
          <span>{label}</span>
        </div>
      </foreignObject>
    </g>
  );
}

function DarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.chartTooltip}>
      {label != null && <div className={styles.ttLabel}>{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey} className={styles.ttRow}>
          <span className={styles.ttName}>{p.name}</span>
          <span className={styles.ttVal}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function formatCompact(n) {
  const x = Number(n) || 0;
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (x >= 10_000) return `${(x / 1000).toFixed(1)}k`;
  if (x >= 1000) return `${(x / 1000).toFixed(2)}k`;
  return String(Math.round(x));
}

function buildEmptyGscChartRows(range) {
  const preset = range?.preset;
  if (range?.start && range?.end) {
    const start = new Date(`${range.start}T12:00:00.000Z`);
    const end = new Date(`${range.end}T12:00:00.000Z`);
    const fmtDay = (d) =>
      d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const fmtHour = (d) =>
      d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
      });
    if (preset === "24h") {
      const a = new Date(`${range.start}T00:00:00.000Z`);
      const b = new Date(`${range.end}T23:00:00.000Z`);
      return [
        {
          name: fmtHour(a),
          clicks: 0,
          impressions: 0,
          sessions: 0,
          users: 0,
        },
        {
          name: fmtHour(b),
          clicks: 0,
          impressions: 0,
          sessions: 0,
          users: 0,
        },
      ];
    }
    return [
      {
        name: fmtDay(start),
        clicks: 0,
        impressions: 0,
        sessions: 0,
        users: 0,
      },
      {
        name: fmtDay(end),
        clicks: 0,
        impressions: 0,
        sessions: 0,
        users: 0,
      },
    ];
  }
  return [
    {
      name: "—",
      clicks: 0,
      impressions: 0,
      sessions: 0,
      users: 0,
    },
    {
      name: "—",
      clicks: 0,
      impressions: 0,
      sessions: 0,
      users: 0,
    },
  ];
}

function syntheticRangeFromPreset(preset) {
  const pad = (n) => String(n).padStart(2, "0");
  const ymdUtc = (d) =>
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const end = new Date();
  if (preset === "24h") {
    const start = new Date(end.getTime() - 24 * 3600 * 1000);
    return {
      start: ymdUtc(start),
      end: ymdUtc(end),
      preset: "24h",
    };
  }
  const days =
    preset === "7d"
      ? 7
      : preset === "180d"
        ? 180
        : preset === "365d"
          ? 365
          : 30;
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days);
  return {
    start: ymdUtc(start),
    end: ymdUtc(end),
    preset: preset || "30d",
  };
}

export default function DashboardAnalytics({
  projectId,
  gscState,
  analyticsRangeKey = "",
  rangePreset = "30d",
}) {
  const { t, i18n } = useTranslation("dashboard");

  const live = gscState?.data?.source === "search_console";
  const loading = gscState?.loading;
  const err = gscState?.error;
  const showBlockingLoader = loading && !gscState?.data;

  const bundle = useMemo(
    () => getAnalyticsBundleForPlanId(projectId),
    [projectId]
  );

  const chartData = useMemo(() => {
    if (live && gscState.data) {
      const raw = gscState.data.series || [];
      if (raw.length) {
        return raw.map((row) => {
          const d = new Date(row.date);
          const name =
            row.granularity === "hour"
              ? d.toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                })
              : d.toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                });
          return {
            name,
            clicks: row.clicks,
            impressions: row.impressions,
            sessions: row.clicks,
            users: row.impressions,
          };
        });
      }
      return buildEmptyGscChartRows(gscState.data.range);
    }
    return buildEmptyGscChartRows(syntheticRangeFromPreset(rangePreset));
  }, [live, gscState?.data, rangePreset]);

  const gscSeriesWasEmpty =
    !live ||
    !gscState.data ||
    !(gscState.data.series && gscState.data.series.length);

  const gscYDomains = useMemo(() => {
    if (!chartData?.length) {
      return { clicks: [0, "auto"], imp: [0, "auto"] };
    }
    const maxC = Math.max(0, ...chartData.map((d) => d.clicks ?? 0));
    const maxI = Math.max(0, ...chartData.map((d) => d.impressions ?? 0));
    return {
      clicks: maxC === 0 ? [0, 1] : [0, "auto"],
      imp: maxI === 0 ? [0, 1] : [0, "auto"],
    };
  }, [chartData]);

  const [showGscClicks, setShowGscClicks] = useState(true);
  const [showGscImpressions, setShowGscImpressions] = useState(true);

  const metrics = useMemo(() => {
    if (live && gscState.data?.totals) {
      const q = gscState.data.totals;
      return [
        {
          key: "clicks",
          value: formatCompact(q.clicks),
          trend: q.clicksTrendPct ?? 0,
          labelKey: "metricClicks",
        },
        {
          key: "impressions",
          value: formatCompact(q.impressions),
          trend: q.impressionsTrendPct ?? 0,
          labelKey: "metricImpressions",
        },
        {
          key: "ctr",
          value: `${(q.ctr ?? 0).toFixed(2)}%`,
          trend: 0,
          labelKey: "metricCtr",
        },
        {
          key: "position",
          value: (q.position ?? 0).toFixed(1),
          trend: 0,
          labelKey: "metricAvgPosition",
        },
      ];
    }
    return [
      {
        key: "clicks",
        value: formatCompact(0),
        trend: 0,
        labelKey: "metricClicks",
      },
      {
        key: "impressions",
        value: formatCompact(0),
        trend: 0,
        labelKey: "metricImpressions",
      },
      {
        key: "ctr",
        value: "0.00%",
        trend: 0,
        labelKey: "metricCtr",
      },
      {
        key: "position",
        value: "0.0",
        trend: 0,
        labelKey: "metricAvgPosition",
      },
    ];
  }, [live, gscState?.data]);

  const sources = useMemo(() => {
    if (live && gscState.data?.sources?.length) return gscState.data.sources;
    if (live) {
      return [{ name: "—", value: 100, color: "#e7e5e4" }];
    }
    return bundle.sources;
  }, [live, gscState?.data?.sources, bundle.sources]);

  const topCountriesRows = useMemo(() => {
    if (!live || !gscState.data?.countries?.length) return [];
    return buildTopCountryRows(gscState.data.countries, i18n.language, 5);
  }, [live, gscState?.data?.countries, i18n.language]);

  const topQueriesRows = useMemo(() => {
    if (!live) return [];
    const q = gscState.data?.queries;
    return q?.length ? q : [];
  }, [live, gscState?.data?.queries]);

  const showTopQueriesTable = topQueriesRows.length > 0;

  const prevTopCountryBarPctRef = useRef({});
  const trendRangeKeyRef = useRef(analyticsRangeKey);

  const topCountriesRowsForUi = useMemo(() => {
    if (trendRangeKeyRef.current !== analyticsRangeKey) {
      trendRangeKeyRef.current = analyticsRangeKey;
      prevTopCountryBarPctRef.current = {};
    }
    return topCountriesRows.map((row) => {
      const prev = prevTopCountryBarPctRef.current[row.code];
      let barTrend = "neutral";
      if (typeof prev === "number" && prev !== row.barPct) {
        barTrend = row.barPct > prev ? "up" : "down";
      }
      return { ...row, barTrend };
    });
  }, [topCountriesRows, analyticsRangeKey]);

  useLayoutEffect(() => {
    const next = { ...prevTopCountryBarPctRef.current };
    for (const r of topCountriesRows) {
      next[r.code] = r.barPct;
    }
    prevTopCountryBarPctRef.current = next;
  }, [topCountriesRows]);

  const topCountriesPieData = useMemo(() => {
    if (!live || !sources?.length) return [];
    const placeholder = sources.length === 1 && sources[0]?.name === "—";
    if (placeholder) return [];
    return sources.map((s) => ({
      ...s,
      name:
        String(s.name) === "Other"
          ? t("analytics.topCountriesOtherSlice")
          : countryNameFromAlpha3(s.name, i18n.language),
    }));
  }, [live, sources, i18n.language, t]);

  const devices = live && gscState.data?.devices?.length
    ? gscState.data.devices
    : bundle.devices;

  if (showBlockingLoader) {
    return (
      <div className={styles.wrap}>
        <p className={styles.note} role="status">
          {t("loadingData")}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`${styles.wrap} ${loading && gscState?.data ? styles.wrapRefreshing : ""}`}
      aria-busy={loading && !!gscState?.data}
    >
      {err?.code === "GSC_NOT_CONNECTED" && (
        <p className={styles.hint}>{t("analytics.gscNotConnected")}</p>
      )}
      {err?.code === "NO_SITE_URL" && (
        <p className={styles.hint}>{t("analytics.gscNoSiteUrl")}</p>
      )}
      {err &&
        err.code &&
        !["GSC_NOT_CONNECTED", "NO_SITE_URL", "GSC_API_ERROR"].includes(
          err.code
        ) && (
          <p className={styles.hint}>
            {t("analytics.gscErrorGeneric")}{" "}
            {err.message ? `(${err.message})` : ""}
          </p>
        )}

      <div className={styles.metricsRow}>
        {metrics.map((m) => (
          <div key={m.key} className={styles.metricCard}>
            <div className={styles.metricTop}>
              <span className={styles.metricValue}>{m.value}</span>
              {m.trend !== 0 && (
                <span
                  className={`${styles.trend} ${
                    m.trend < 0 ? styles.trendDown : ""
                  }`}
                >
                  <TrendingUp
                    size={14}
                    className={m.trend < 0 ? styles.trendIconFlip : ""}
                  />
                  {t("analytics.metricTrendUp", {
                    value: Math.abs(m.trend),
                  })}
                </span>
              )}
            </div>
            <div className={styles.metricLabel}>
              {m.label ?? t(`analytics.${m.labelKey}`)}
            </div>
            {live && m.key === "clicks" && gscState.data?.totals && (
              <div className={styles.metricSub}>
                {t("analytics.metricPreviously", {
                  value: formatCompact(gscState.data.totals.prevClicks ?? 0),
                })}
              </div>
            )}
            {live && m.key === "impressions" && gscState.data?.totals && (
              <div className={styles.metricSub}>
                {t("analytics.metricPreviously", {
                  value: formatCompact(gscState.data.totals.prevImpressions ?? 0),
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.analyticsGrid}>
        <div className={`${styles.chartCard} ${styles.gridLeftTop}`}>
          <h3 className={styles.chartTitle}>
            {t("analytics.chartSearchPerformanceTitle")}
          </h3>
          {chartData?.length ? (
            <>
              <div className={styles.gscToggleRow}>
                <button
                  type="button"
                  className={`${styles.gscToggleCard} ${
                    showGscClicks ? styles.gscToggleCardClicksOn : ""
                  }`}
                  onClick={() => setShowGscClicks((v) => !v)}
                  aria-pressed={showGscClicks}
                >
                  <span className={styles.gscToggleCheck} aria-hidden>
                    {showGscClicks ? "✓" : ""}
                  </span>
                  <span className={styles.gscToggleLabel}>
                    {t("analytics.metricClicks")}
                  </span>
                  <span className={styles.gscToggleValue}>
                    {formatCompact(gscState.data?.totals?.clicks ?? 0)}
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.gscToggleCard} ${
                    showGscImpressions ? styles.gscToggleCardImpOn : ""
                  }`}
                  onClick={() => setShowGscImpressions((v) => !v)}
                  aria-pressed={showGscImpressions}
                >
                  <span className={styles.gscToggleCheck} aria-hidden>
                    {showGscImpressions ? "✓" : ""}
                  </span>
                  <span className={styles.gscToggleLabel}>
                    {t("analytics.metricImpressions")}
                  </span>
                  <span className={styles.gscToggleValue}>
                    {formatCompact(gscState.data?.totals?.impressions ?? 0)}
                  </span>
                </button>
              </div>
              {gscSeriesWasEmpty && live ? (
                <p className={styles.chartEmptyHint} role="status">
                  {t("analytics.chartNoDataInRange")}
                </p>
              ) : null}
              <div className={`${styles.chartArea} ${styles.chartAreaGsc}`}>
                <ResponsiveContainer
                  key={analyticsRangeKey || "gsc-chart"}
                  width="100%"
                  height="100%"
                >
                  <LineChart
                    data={chartData}
                    margin={{ top: 4, right: 8, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid
                      stroke="#e8eaed"
                      vertical={false}
                      strokeDasharray="0"
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#5f6368", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      yAxisId="clicks"
                      orientation="left"
                      tick={{ fill: GSC_BLUE, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                      domain={gscYDomains.clicks}
                    />
                    <YAxis
                      yAxisId="impressions"
                      orientation="right"
                      tick={{ fill: GSC_PURPLE, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                      domain={gscYDomains.imp}
                    />
                    <Tooltip content={<DarkTooltip />} />
                    {showGscClicks ? (
                      <Line
                        yAxisId="clicks"
                        type="monotone"
                        dataKey="clicks"
                        name={t("analytics.metricClicks")}
                        stroke={GSC_BLUE}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ) : null}
                    {showGscImpressions ? (
                      <Line
                        yAxisId="impressions"
                        type="monotone"
                        dataKey="impressions"
                        name={t("analytics.metricImpressions")}
                        stroke={GSC_PURPLE}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ) : null}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : null}
        </div>

        <div className={`${styles.chartCard} ${styles.gridRightTop}`}>
          <div className={styles.topCountriesTitleRow}>
            <h3 className={styles.chartTitle}>
              {live
                ? t("analytics.topCountriesTitle")
                : t("analytics.chartSourcesTitle")}
            </h3>
            {live ? (
              <span
                className={styles.topCountriesHelp}
                title={t("analytics.topCountriesHelp")}
                aria-label={t("analytics.topCountriesHelp")}
                role="img"
              >
                <CircleHelp size={16} strokeWidth={2} aria-hidden />
              </span>
            ) : null}
          </div>
          {live && topCountriesRows.length > 0 ? (
            <>
              <div className={styles.topCountries}>
                <div className={styles.topCountriesHeader}>
                  <span>{t("analytics.topCountriesColCountry")}</span>
                  <span
                    aria-hidden
                    className={styles.topCountriesHeadSpacer}
                  />
                  <span>{t("analytics.topCountriesColClicks")}</span>
                </div>
                <ul className={styles.topCountriesList}>
                  {topCountriesRowsForUi.map((row) => (
                    <li key={row.code} className={styles.topCountriesRow}>
                      <div className={styles.topCountriesLeft}>
                        {row.flagSrc ? (
                          <img
                            src={row.flagSrc}
                            alt=""
                            width={28}
                            height={21}
                            className={styles.topCountriesFlagImg}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <span
                            className={styles.topCountriesFlagFallback}
                            aria-hidden
                          >
                            —
                          </span>
                        )}
                        <span className={styles.topCountriesName}>
                          {row.name}
                        </span>
                      </div>
                      <div className={styles.topCountriesBarCell}>
                        <div
                          className={styles.topCountriesBarTrack}
                          role="presentation"
                        >
                          <div
                            className={styles.topCountriesBarFill}
                            data-trend={row.barTrend}
                            style={{ width: `${row.barPct}%` }}
                          />
                        </div>
                      </div>
                      <span
                        className={`${styles.topCountriesPct} ${
                          row.barTrend === "up"
                            ? styles.topCountriesPctUp
                            : row.barTrend === "down"
                              ? styles.topCountriesPctDown
                              : ""
                        }`}
                      >
                        {row.pctLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {topCountriesPieData.length > 0 ? (
                <div className={styles.topCountriesPieWrap}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={topCountriesPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={68}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {topCountriesPieData.map((entry, i) => (
                          <Cell
                            key={`${entry.color}-${i}`}
                            fill={entry.color}
                            stroke="transparent"
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<DarkTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: "11px", color: "#57534e" }}
                        formatter={(value) => (
                          <span className={styles.legendText}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
            </>
          ) : live ? (
            <p className={styles.chartEmptyHint} role="status">
              {t("analytics.topCountriesEmpty")}
            </p>
          ) : (
            <div className={styles.chartAreaPie}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sources}
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                  >
                    {sources.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={entry.color}
                        stroke="transparent"
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<DarkTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "12px", color: "#57534e" }}
                    formatter={(value) => (
                      <span className={styles.legendText}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className={`${styles.chartCard} ${styles.gridLeftBottom}`}>
          <div className={styles.topCountriesTitleRow}>
            <h3 className={styles.chartTitle}>
              {t("analytics.topQueriesTitle")}
            </h3>
            <span
              className={styles.topCountriesHelp}
              title={t("analytics.topQueriesHelp")}
              aria-label={t("analytics.topQueriesHelp")}
              role="img"
            >
              <CircleHelp size={16} strokeWidth={2} aria-hidden />
            </span>
          </div>
          {showTopQueriesTable ? (
            <div className={styles.topQueries}>
              <div className={styles.topQueriesHeader}>
                <span>{t("analytics.topQueriesColQuery")}</span>
                <span
                  aria-hidden
                  className={styles.topCountriesHeadSpacer}
                />
                <span>{t("analytics.topQueriesColClicks")}</span>
                <span>{t("analytics.topQueriesColImpressions")}</span>
              </div>
              <div className={styles.topQueriesScroll}>
                <ul className={styles.topCountriesList}>
                  {topQueriesRows.map((row, i) => (
                    <li
                      key={`q-${i}-${String(row.query).slice(0, 48)}`}
                      className={styles.topQueriesRow}
                    >
                      <span className={styles.topQueriesQuery} title={row.query}>
                        {row.query}
                      </span>
                      <div className={styles.topCountriesBarCell}>
                        <div
                          className={styles.topCountriesBarTrack}
                          role="presentation"
                        >
                          <div
                            className={styles.topQueriesBarFill}
                            style={{ width: `${row.barPct ?? 0}%` }}
                          />
                        </div>
                      </div>
                      <span className={styles.topQueriesClicks}>
                        {formatCompact(row.clicks)}
                      </span>
                      <span className={styles.topQueriesImpr}>
                        {formatCompact(row.impressions ?? 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className={styles.topQueriesMessage} role="status">
              {live
                ? t("analytics.topQueriesEmpty")
                : t("analytics.topQueriesOnlyLive")}
            </p>
          )}
        </div>

        <div className={`${styles.chartCard} ${styles.gridRightBottom}`}>
        <h3 className={styles.chartTitle}>
          {live
            ? t("analytics.chartDevicesTitleGsc")
            : t("analytics.chartDevicesTitle")}
        </h3>
        <div className={styles.chartAreaBar}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={devices}
              layout="vertical"
              margin={{ top: 14, right: 16, left: 12, bottom: 10 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e7e5e4"
                opacity={0.9}
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fill: "#78716c", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={92}
                interval={0}
                tick={DeviceYAxisTick}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="sessions" name="%" radius={[0, 6, 6, 0]}>
                {devices.map((_, i) => (
                  <Cell
                    key={`d-${i}`}
                    fill={["#17becf", "#bcbd22", "#8c564b"][i % 3]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      </div>
    </div>
  );
}
